import type {
  Audiogram,
  DinTrial,
  PatientInfo,
  Platform,
  QuestionnaireAnswers,
  ResultCategory,
  ScreenerConfig,
  ToneTrial,
} from "@hearing/shared";

const BASE = (import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8080").replace(
  /\/$/,
  "",
);

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    let detail: unknown;
    try {
      detail = await res.json();
    } catch {
      detail = await res.text();
    }
    throw new ApiError(res.status, detail);
  }
  return (await res.json()) as T;
}

export class ApiError extends Error {
  status: number;
  detail: unknown;
  constructor(status: number, detail: unknown) {
    super(`API error ${status}`);
    this.status = status;
    this.detail = detail;
  }
}

export interface ScreeningSubmission {
  platform: Platform;
  patient: PatientInfo;
  answers: QuestionnaireAnswers;
  srtDb?: number;
  trials?: DinTrial[];
  perEar?: Record<string, number>;
  audiogram?: Audiogram;
  toneTrials?: ToneTrial[];
  /** `utm_source` captured from the screener URL on first load. */
  utmSource?: string;
}

export const api = {
  getConfig: () =>
    request<ScreenerConfig>(
      `/api/config?platform=${encodeURIComponent(detectPlatform())}`,
    ),
  createScreening: (submission: ScreeningSubmission) =>
    request<{ category: ResultCategory }>("/api/screenings", {
      method: "POST",
      body: JSON.stringify(submission),
    }),
};

export const bookingFallback = import.meta.env.VITE_BOOKING_URL ?? null;

function detectPlatform(): string {
  const requested = new URLSearchParams(window.location.search).get("platform");
  if (requested) return requested;

  const hostname = window.location.hostname.toLowerCase().replace(/^www\./, "");
  const byHostname: Record<string, Platform> = {
    "clearearwax.co.uk": "clear-earwax",
    "earwax.org.uk": "earwax-org-uk",
    "hearingexpert.co.uk": "hearing-expert",
  };
  return byHostname[hostname] ?? "hearing-expert";
}
